import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  faUsers, 
  faCalendarAlt, 
  faQrcode 
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/Admin.css';
import PageHeader from '../common/PageHeader';
import { useAnalytics, useScrollTracking } from '../../hooks/useAnalytics';

import AdminGrid from '../admin/AdminGrid';
import AdminNavigation from '../admin/AdminNavigation';
import UserManagementDashboard from '../admin/UserManagementDashboard';
import EventsDashboard from '../admin/EventsDashboard';
import ApplicationsDashboard from '../admin/ApplicationsDashboard';
import QrScanner from './QrScanner';

// Type for the admin section selection
type AdminSection = 'grid' | 'users' | 'events' | 'event-applications' | 'scanner';

interface AdminSectionDetails {
  id: AdminSection;
  name: string;
  component: React.ReactNode;
  showBackButton: boolean;
}

const Admin: React.FC = () => {
  const { trackPageView, trackCustomEvent } = useAnalytics();
  useScrollTracking(); // Enable scroll tracking
  
  const [activeSection, setActiveSection] = useState<AdminSection>('grid');
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>(undefined);
  const [navigationHistory, setNavigationHistory] = useState<AdminSection[]>(['grid']);

  // Use useCallback to memoize navigation functions
  const navigateTo = useCallback((section: AdminSection) => {
    // Track admin navigation action
    trackCustomEvent('admin_navigation', {
      from_section: activeSection,
      to_section: section,
      nav_type: 'direct'
    });
    
    setNavigationHistory(prev => [...prev, section]);
    setActiveSection(section);
  }, [activeSection, trackCustomEvent]);

  // Function to handle back navigation with useCallback
  const navigateBack = useCallback(() => {
    // Remove the current section and set the previous one
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current section
      const previousSection = newHistory[newHistory.length - 1];
      
      // Track admin back navigation
      trackCustomEvent('admin_navigation', {
        from_section: activeSection,
        to_section: previousSection,
        nav_type: 'back'
      });
      
      setNavigationHistory(newHistory);
      setActiveSection(previousSection);
    }
  }, [navigationHistory, activeSection, trackCustomEvent]);

  // Function to handle navigation to an event's applications with useCallback
  const handleViewEventApplications = useCallback((eventId: string) => {
    // Track viewing applications for a specific event
    trackCustomEvent('admin_view_event_applications', {
      event_id: eventId,
      from_section: activeSection
    });
    
    setSelectedEventId(eventId);
    navigateTo('event-applications');
  }, [activeSection, navigateTo, trackCustomEvent]);

  // Use useMemo for admin sections to prevent unnecessary re-creation
  const adminSections = useMemo<Record<AdminSection, AdminSectionDetails>>(() => ({
    'grid': {
      id: 'grid',
      name: 'Admin Dashboard',
      component: (
        <AdminGrid
          sections={[
            {
              title: 'User Management Dashboard',
              icon: faUsers,
              description: 'Manage users, view profiles, and update account settings',
              onClick: () => navigateTo('users')
            },
            {
              title: 'Events Dashboard',
              icon: faCalendarAlt,
              description: 'Create, edit, and manage events and their applications',
              onClick: () => navigateTo('events')
            },
            {
              title: 'QR Code Scanner',
              icon: faQrcode,
              description: 'Scan QR codes for event check-ins and verifications',
              onClick: () => navigateTo('scanner')
            }
          ]}
        />
      ),
      showBackButton: false
    },
    'users': {
      id: 'users',
      name: 'User Management Dashboard',
      component: <UserManagementDashboard />,
      showBackButton: true
    },
    'events': {
      id: 'events',
      name: 'Events Dashboard',
      component: <EventsDashboard onViewApplications={handleViewEventApplications} />,
      showBackButton: true
    },
    'event-applications': {
      id: 'event-applications',
      name: 'Event Applications',
      component: (
        <ApplicationsDashboard 
          eventId={selectedEventId} 
          showBackButton={false} // We'll use our AdminNavigation instead
        />
      ),
      showBackButton: true
    },
    'scanner': {
      id: 'scanner',
      name: 'QR Code Scanner',
      component: <QrScanner />,
      showBackButton: true
    }
  }), [navigateTo, handleViewEventApplications, selectedEventId]);

  // Track admin page view when section changes
  useEffect(() => {
    const currentSectionName = adminSections[activeSection]?.name || 'Admin Dashboard';
    
    trackPageView({ 
      page_title: `Admin: ${currentSectionName}`,
      page_location: window.location.href 
    });

    trackCustomEvent('admin_section_viewed', {
      section: activeSection,
      section_name: currentSectionName
    });
  }, [activeSection, trackPageView, trackCustomEvent, adminSections]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // If there is more than one item in navigationHistory, treat as internal back
      if (navigationHistory.length > 1) {
        navigateBack();
        // Prevent default browser back
        window.history.pushState(null, '', window.location.href);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    // Push a dummy state to the history stack so popstate triggers
    window.history.pushState(null, '', window.location.href);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigationHistory, navigateBack]);

  // Get the current section
  const currentSection = adminSections[activeSection];

  return (
    <>
      <PageHeader
        title="Admin Dashboard"
        subtitle="Manage users, events applications and more..."
      />
      
      {/* Show navigation bar for non-grid sections */}
      {activeSection !== 'grid' && (
        <AdminNavigation 
          currentSection={currentSection.name}
          showBackButton={currentSection.showBackButton}
          onBack={navigateBack}
        />
      )}
      
      {/* Render the current section component */}
      {currentSection.component}
    </>
  );
};

export default Admin;